import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, LogOut, User } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import NotificationBell from './NotificationBell';

const roleLabel = {
  student: 'Student',
  admin: 'Admin',
  instructor: 'Instructor',
  agency: 'Agency',
};

/**
 * Pages whose name is not their address.
 *
 * The title is normally derived from the URL, which is right almost
 * everywhere and cheap. Website Engagement is the exception: its pages were
 * renamed and their URLs deliberately were not, so that every link already
 * sent still works. Without this the topbar would keep announcing the old
 * name over the new heading.
 *
 * Longest prefix first, so a child route is matched before its parent.
 */
export const TITLE_OVERRIDES = [
  ['/admin/website-engagement/contact-inquiries', 'Contact Form Inquiries'],
  ['/admin/website-engagement/newsletter', 'Newsletter Subscribers'],
  ['/admin/website-engagement/overview', 'Website Engagement'],
  ['/admin/website-engagement', 'Website Engagement'],
  ['/admin/website-messages', 'Online Chat Messages'],
  ['/admin/website-signups', 'Event Signups'],
];

export default function Topbar({ onMenuClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, signOut } = useAuth();

  const getPageTitle = () => {
    const path = location.pathname;
    const override = TITLE_OVERRIDES.find(([prefix]) => (
      path === prefix || path.startsWith(`${prefix}/`)
    ));
    if (override) return override[1];
    const segments = path.split('/').filter(Boolean);
    if (segments.length >= 2) {
      return segments[1].replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    }
    return 'Dashboard';
  };

  const displayName = profile?.full_name || profile?.email || 'User';
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    toast.success('Signed out');
    navigate('/login', { replace: true });
  };

  const handleProfile = () => {
    if (profile?.role === 'admin') navigate('/admin/profile');
    else if (profile?.role === 'instructor') navigate('/instructor/profile');
    else if (profile?.role === 'agency') navigate('/agency/profile');
    else navigate('/student/profile');
  };

  return (
    <div className="h-14 flex items-center justify-between px-4 sm:px-6 bg-[#062B1A] border-b border-white/10 flex-shrink-0">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden text-white hover:bg-white/10 hover:text-white"
          onClick={onMenuClick}
          data-testid="mobile-menu-button"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <h1
          className="text-lg font-semibold text-white hidden sm:block"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {getPageTitle()}
        </h1>
      </div>

      <div className="flex items-center gap-3">
        {/* Role badge — read-only, replaces the old role switcher */}
        {profile?.role && (
          <Badge
            variant="outline"
            data-testid="role-badge"
            className="hidden sm:inline-flex h-9 px-3 border-[#D4AF37]/50 bg-white/5 text-white font-semibold text-xs gap-2"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
            {roleLabel[profile.role] || profile.role}
          </Badge>
        )}

        <NotificationBell />

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 rounded-full hover:bg-white/10 px-1.5 py-1 transition-colors"
              data-testid="user-menu-trigger"
            >
              <Avatar className="w-8 h-8 border border-[#D4AF37]/40">
                <AvatarFallback className="bg-[#D4AF37] text-[#062B1A] text-xs font-semibold">
                  {initials || <User className="w-4 h-4" />}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-[#062B1A]">{displayName}</span>
                <span className="text-xs font-normal text-[#062B1A]/60">
                  {profile?.email}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#062B1A]/40">
              Signed in as {roleLabel[profile?.role] || 'User'}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleProfile}
              data-testid="user-menu-profile"
              className="text-[#062B1A] cursor-pointer"
            >
              <User className="w-4 h-4 mr-2" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleSignOut}
              data-testid="user-menu-signout"
              className="text-[#062B1A] cursor-pointer"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
